import React, { useRef, useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Box, Text, HStack, Button, Icon, Spinner } from "@chakra-ui/react";
import { Link, useNavigate } from "react-router-dom"; // Import Link and useNavigate for navigation
import axios from "axios";
import { FaLightbulb, FaWrench, FaTag } from "react-icons/fa"; // Additional icons
import { DynamicIcon } from "../utils/iconMapper";
import WhyUsBox from "../components/miscellaneous/WhyUs";
import StyledAccordion from "../components/miscellaneous/Accordion";

// IconBox component with modern design
const IconBox = ({ icon, label }) => {
  return (
    <Link to={`/category/${label}`}>
      <Box
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="xl"
        p={6}
        textAlign="center"
        boxShadow="sm"
        _hover={{
          boxShadow: "xl",
          borderColor: "orange.400",
          transform: "translateY(-4px)",
        }}
        transition="all 0.3s ease"
        width={{ base: "140px", md: "180px" }}
        height="140px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        cursor="pointer"
      >
        <DynamicIcon iconName={icon} size={48} color="#f97316" />
        <Text mt={3} fontWeight="600" color="gray.800" fontSize="sm">
          {label}
        </Text>
      </Box>
    </Link>
  );
};

const Landing = () => {
  const scrollRef = useRef();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);

  // Fetch categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get("/api/category?active=true");
        setCategories(response.data);
        setLoadingCategories(false);
      } catch (error) {
        console.error("Error fetching categories:", error);
        setLoadingCategories(false);
      }
    };

    fetchCategories();
  }, []);

  const scroll = (direction) => {
    const scrollAmount = direction === "left" ? -300 : 300;
    scrollRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const scrollToWhyChoose = () => {
    const element = document.getElementById("why-choose-section");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      <Helmet>
        <title>ATO | Find Trusted Home Services</title>
        <meta
          name="description"
          content="Browse and book trusted professionals for any home service. Discover why ATO is your one-stop app for all trades and home needs."
        />
      </Helmet>
      <Box bg="gray.50" minH="100vh">
        {/* Hero Section */}
        <Box
          bg="white"
          pt={{ base: "60px", md: "100px" }}
          pb={{ base: "60px", md: "100px" }}
          px={{ base: 4, md: 8 }}
          textAlign="center"
          borderBottom="1px solid"
          borderColor="gray.200"
        >
          <Text
            fontSize={{ base: "4xl", md: "6xl" }}
            fontWeight="800"
            color="gray.900"
            fontFamily="'Work Sans', sans-serif"
            mb={4}
          >
            All Trades. One App.
          </Text>
          <Text
            fontSize={{ base: "lg", md: "xl" }}
            color="gray.600"
            maxW="700px"
            mx="auto"
            mb={8}
          >
            Find trusted professionals for any home service
          </Text>
          <HStack spacing={4} justify="center" wrap="wrap">
            <Button
              size="lg"
              bg="orange.500"
              color="white"
              _hover={{ bg: "orange.600" }}
              borderRadius="md"
              px={8}
              fontWeight="600"
              onClick={() => navigate("/login")}
              cursor="pointer"
            >
              Get Started
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor="gray.300"
              color="gray.700"
              _hover={{ bg: "gray.50" }}
              borderRadius="md"
              px={8}
              fontWeight="600"
              onClick={scrollToWhyChoose}
              cursor="pointer"
            >
              Learn More
            </Button>
          </HStack>
        </Box>

        {/* Browse Services Section */}
        <Box py={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
          <Text
            textAlign="center"
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="700"
            color="gray.900"
            fontFamily="'Work Sans', sans-serif"
            mb={12}
          >
            Browse Services
          </Text>

          <Box
            display="grid"
            gridTemplateColumns={{
              base: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
              xl: "repeat(5, 1fr)",
            }}
            gap={6}
            maxW="1200px"
            mx="auto"
          >
            {loadingCategories ? (
              <Box
                gridColumn="1 / -1"
                display="flex"
                justifyContent="center"
                py={8}
              >
                <Spinner size="xl" color="orange.500" thickness="4px" />
              </Box>
            ) : categories.length > 0 ? (
              categories.map((category) => (
                <IconBox
                  key={category._id}
                  icon={category.icon}
                  label={category.name}
                />
              ))
            ) : (
              <Box gridColumn="1 / -1" textAlign="center" py={8}>
                <Text color="gray.500" fontSize="lg">
                  No services available
                </Text>
              </Box>
            )}
          </Box>
        </Box>

        {/* Why Choose Section */}
        <Box
          id="why-choose-section"
          bg="gray.50"
          py={{ base: 16, md: 20 }}
          px={{ base: 4, md: 8 }}
          position="relative"
        >
          <Box maxW="1200px" mx="auto">
            <Text
              textAlign="center"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="700"
              color="gray.900"
              fontFamily="'Work Sans', sans-serif"
              mb={3}
            >
              Why Choose Us?
            </Text>
            <Text
              textAlign="center"
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              maxW="700px"
              mx="auto"
              mb={12}
            >
              We're committed to providing exceptional service and expertise
            </Text>
            <Box
              display="grid"
              gridTemplateColumns={{
                base: "1fr",
                md: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              }}
              gap={8}
              justifyItems="center"
            >
              <WhyUsBox
                icon={FaLightbulb}
                title="Testimonials"
                description="Still not convinced? Take a moment to see what our happy customers are saying about us."
                linkText="Read For Yourself"
                linkUrl="#"
              />
              <WhyUsBox
                icon={FaWrench}
                title="Tricks of the Trade"
                description="Learn the most common problems our team faces and the simplest solutions to save you time."
                linkText="Check Them Out"
                linkUrl="/tricks-of-the-trade"
              />
              <WhyUsBox
                icon={FaTag}
                title="Our Promise"
                description="From quick fixes to complete transformations, we’re committed to making your home better than ever."
                linkText="Discover What We Can Promise"
                linkUrl="/our-promise"
              />
            </Box>
          </Box>
        </Box>

        {/* FAQ Section */}
        <Box bg="white" py={{ base: 16, md: 20 }} px={{ base: 4, md: 8 }}>
          <Box maxW="1000px" mx="auto">
            <Text
              textAlign="center"
              fontSize={{ base: "3xl", md: "4xl" }}
              fontWeight="700"
              color="gray.900"
              fontFamily="'Work Sans', sans-serif"
              mb={3}
            >
              Frequently Asked Questions
            </Text>
            <Text
              textAlign="center"
              fontSize={{ base: "md", md: "lg" }}
              color="gray.600"
              maxW="700px"
              mx="auto"
              mb={12}
            >
              Got questions? We've got answers
            </Text>
            <StyledAccordion />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Landing;
